<template>
  <div
    ref="reference"
    class="base-tooltip"
    @mouseenter="handleHover(true)"
    @mouseleave="handleHover(false)"
  >
    <div class="base-tooltip__display" @click="handleClick">
      <slot name="display" />
    </div>
    <div
      v-if="!disabled"
      ref="tooltip"
      class="base-tooltip__content"
      role="tooltip"
      :class="[
        `base-tooltip__content-color--${tooltipColor}`,
        contentWidth ? `base-tooltip__content--width-${contentWidth}` : '',
        {
          'base-tooltip__content--shadow': shadow,
          'base-tooltip__content--italic': italic,
          'base-tooltip__content--centered': centered,
          'base-tooltip__content--is-open': isOpen,
          'base-tooltip__content--from-article': fromArticle,
          'base-tooltip__content--round': round,
        },
      ]"
      :style="floatingStyles"
    >
      <slot name="tooltip" />

      <div
        v-if="arrow && !fromArticle"
        ref="floatingArrow"
        class="base-tooltip__floating-arrow"
        :class="[
          `base-tooltip__floating-arrow--${middlewareData.offset?.placement}`,
        ]"
        :style="{
          position: 'absolute',
          left:
            middlewareData.arrow?.x != null
              ? `${middlewareData.arrow.x}px`
              : '',
          top:
            middlewareData.arrow?.y != null
              ? `${middlewareData.arrow.y}px`
              : '',
        }"
      />
    </div>
  </div>
</template>

<script setup>
  import { ref, onMounted, onBeforeUnmount } from 'vue';
  import {
    autoUpdate,
    arrow,
    useFloating,
    flip,
    shift,
    offset,
  } from '@floating-ui/vue';

  const props = defineProps({
    placement: {
      type: String,
      required: false,
      default: 'right',
      validator: (value) => {
        const PLACEMENTS = ['top', 'right', 'bottom', 'left'];
        return PLACEMENTS.includes(value);
      },
    },
    xAlign: {
      type: String,
      required: false,
      default: 'center',
      validator: (value) => {
        const ALIGNMENTS = ['left', 'center', 'right'];
        return ALIGNMENTS.includes(value);
      },
    },
    yAlign: {
      type: String,
      required: false,
      default: 'top',
      validator: (value) => {
        const ALIGNMENTS = ['top', 'bottom'];
        return ALIGNMENTS.includes(value);
      },
    },
    trigger: {
      type: String,
      required: false,
      default: 'hover',
      validator: (value) => {
        const TRIGGERS = ['hover', 'click'];
        return TRIGGERS.includes(value);
      },
    },
    tooltipColor: {
      type: String,
      required: false,
      default: 'grey',
      validator: (value) => {
        const COLORS = ['grey', 'white'];
        return COLORS.includes(value);
      },
    },
    contentWidth: {
      type: String,
      required: false,
      default: '',
      validator: (value) => {
        if (!value) return true;
        const WIDTHS = ['fit-content', 'large', 'medium', 'small'];
        return WIDTHS.includes(value);
      },
    },
    disabled: Boolean,
    shadow: Boolean,
    arrow: Boolean,
    italic: Boolean,
    centered: Boolean,
    round: Boolean,
    fromArticle: {
      type: Boolean,
      default: false,
    },
  });

  const isOpen = ref(false);
  const reference = ref(null);
  const tooltip = ref(null);
  const floatingArrow = ref(null);
  const inFocus = ref(false);
  const amount = props.fromArticle ? 2 : 10;
  const { floatingStyles, middlewareData } = useFloating(reference, tooltip, {
    open: isOpen,
    whileElementsMounted: autoUpdate,
    placement: props.placement,
    middleware: [
      flip(),
      offset(amount),
      shift({ padding: 8 }),
      arrow({ element: floatingArrow }),
    ],
  });

  onMounted(() => {
    // MOVE THE TOOL TIP FROM HERE TO THE END OF THE BODY
    if (tooltip.value) {
      document.body.appendChild(tooltip.value);
    }

    window.addEventListener('click', (event) => {
      if (
        reference.value &&
        reference.value &&
        !reference.value.contains(event.target) &&
        tooltip.value &&
        !tooltip.value.contains(event.target)
      ) {
        isOpen.value = false;
      }
    });
  });

  onBeforeUnmount(() => {
    if (tooltip.value && tooltip.value) {
      document.body.removeChild(tooltip.value);
    }

    window.removeEventListener('click', (event) => {
      if (
        !reference.value.contains(event.target) &&
        !tooltip.value.contains(event.target)
      ) {
        isOpen.value = false;
      }
    });
  });

  function handleClick() {
    if (props.trigger === 'click' && !props.disabled) {
      isOpen.value = !isOpen.value;
    }
  }
  function handleHover(isHovering) {
    if (isHovering) {
      inFocus.value = isHovering;
      setTimeout(() => {
        if (props.trigger === 'hover' && !props.disabled && inFocus.value) {
          isOpen.value = isHovering;
        }
      }, 800);
    } else {
      if (props.trigger === 'hover' && !props.disabled) {
        inFocus.value = isHovering;
        isOpen.value = isHovering;
      }
    }
  }
</script>

<style lang="scss" scoped>
  .base-tooltip {
    display: inline-block;
    position: relative;

    &__display {
      display: inline-block;
    }

    &__content {
      position: absolute;
      padding: 16px;
      border-radius: 0.25rem;
      border: 1px solid #d1d5db;
      opacity: 0;
      visibility: hidden;
      transition: all 300ms ease;
      z-index: 100;

      font-size: 14px;
      font-weight: 400;
      line-height: 1.8;

      width: 100vw;
      max-width: 364px;

      &--round {
        border-radius: 0.75rem;
      }

      &--from-article {
        padding: 8px;
        font-size: 12px;
        border-radius: 8px;
        top: -7px !important;
        ::before {
          content: '';
          position: absolute;
          bottom: 0%;
          left: 50%;
          transform: translate(-50%, 65%) rotate(45deg);
          width: 10px;
          height: 10px;
          background-color: #ffffff;
          border-right: 1px solid #d1d5db;
          border-bottom: 1px solid #d1d5db;
        }
      }

      &--is-open {
        opacity: 1;
        visibility: visible;
      }

      &--centered {
        text-align: center;
      }

      &-color {
        &--grey {
          background-color: #eef1f4;
          color: #585b60;

          --arrow-color: #eef1f4;
        }
        &--white {
          background-color: var(--itr-white);
          color: var(--itr-grey-charcoal);

          --arrow-color: var(--itr-white);
        }
      }

      &--shadow {
        box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
      }

      &--width-fit-content {
        width: max-content;
        max-width: inherit;
      }
      &--width-large {
        max-width: 800px;
      }
      &--width-medium {
        max-width: 475px;
      }
      &--width-small {
        max-width: 144px;
      }

      &--italic {
        font-style: italic;
      }
    }

    &__floating-arrow {
      position: absolute;
      width: 0;
      height: 0;

      &--top {
        top: 100%;

        &::before {
          content: '';
          position: absolute;
          bottom: 0%;
          left: 50%;
          transform: translate(-50%, 65%) rotate(45deg);
          width: 10px;
          height: 10px;
          background-color: var(--arrow-color);
          border-right: 1px solid #d1d5db;
          border-bottom: 1px solid #d1d5db;
        }
      }
      &--bottom {
        bottom: 100%;

        &::before {
          content: '';
          position: absolute;
          bottom: 0%;
          left: 50%;
          transform: translate(-50%, 50%) rotate(-45deg);
          width: 10px;
          height: 10px;
          background-color: var(--arrow-color);
          border-right: 1px solid #d1d5db;
          border-top: 1px solid #d1d5db;
        }
      }
      &--left {
        left: 100%;

        &::before {
          content: '';
          position: absolute;
          bottom: 0%;
          left: 50%;
          transform: translate(-50%, 50%) rotate(45deg);
          width: 10px;
          height: 10px;
          background-color: var(--arrow-color);
          border-right: 1px solid #d1d5db;
          border-top: 1px solid #d1d5db;
        }
      }
      &--right {
        right: 100%;

        &::before {
          content: '';
          position: absolute;
          bottom: 0%;
          left: 50%;
          transform: translate(-50%, 50%) rotate(-135deg);
          width: 10px;
          height: 10px;
          background-color: var(--arrow-color);
          border-right: 1px solid #d1d5db;
          border-top: 1px solid #d1d5db;
        }
      }
    }
  }
</style>
